import * as React from "react";

/**
 * Run an effect only once.
 *
 * @param effect The effect callback to run.
 */
export function useEffectOnce(
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	effect: () => void | (() => void) | undefined,
): void {
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	const destroyFunction = React.useRef<void | (() => void) | undefined>(
		undefined,
	);
	const effectCalled = React.useRef(false);
	const renderAfterCalled = React.useRef(false);
	const [, setValue] = React.useState<number>(0);

	if (effectCalled.current) {
		renderAfterCalled.current = true;
	}

	React.useEffect(() => {
		// only execute the effect first time around
		if (!effectCalled.current) {
			destroyFunction.current = effect();
			effectCalled.current = true;
		}

		// this forces one render after the effect is run
		setValue((value) => value + 1);

		return () => {
			// if the comp didn't render since the useEffect was called,
			// we know it's the dummy React cycle
			if (!renderAfterCalled.current) {
				return;
			}
			if (destroyFunction.current) {
				destroyFunction.current();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}
